import React, { useState } from 'react'
import { apiGet } from '../../../generic/Api_Functions'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { DefaultAccordion } from '../../../generic/DefaultAccordion'
import { useSmartInterval } from '../../../generic/hooks/useSmartInterval'
import SmartMateriaUITable from '../../../generic/SmartMateriaUITable'
import { CircularProgress, Stack, Typography } from '@mui/material'
import { useTheme } from '@emotion/react'

const Assets = () => {
  const { t_ } = useSmartTranslation()
  const [assets, setAssets] = useState(null)
  const theme = useTheme()

  const getRowBackgroundColorForTable = (row) => {
    if (row.spindle) {
      return theme.palette.background.green + '60'
    }
    return null
  }

  const assetsInterval = useSmartInterval(
    async () => {
      return await apiGet('toolTable/assets')
    },
    5000,
    (data) => {
      if (data != null) {
        setAssets(data)
      }
    }, false)

  const toolColumns = [
    {
      name: t_('Order'),
      field: 'order',
      hide: true
    },
    {
      name: t_('Code'),
      field: 'code'
    },
    {
      name: t_('Tool number'),
      field: 'toolNumber'
    },
    {
      name: t_('Tool name'),
      field: 'toolName'
    },
    {
      name: t_('Replacement tool'),
      field: 'twinToolNumber'
    },
    {
      name: t_('Life') + '\n (' + t_('Current/Total') + ')',
      field: 'currentLifeConverted'
    },
    {
      name: t_('Available'),
      field: 'available',
      type: 'boolean'
    },
    {
      name: t_('Holder'),
      field: 'fixture.name'
    },
    {
      name: t_('ATC'),
      field: 'atc'
    },
    {
      name: t_('Shelf'),
      field: 'shelveStoragePosition.shelve'
    },
    {
      name: t_('Row'),
      field: 'shelveStoragePosition.position'
    },
    {
      name: t_('Column'),
      field: 'shelveStoragePosition.depth'
    }
  ]

  const millingColumns = [
    {
      name: t_('Code'),
      field: 'code'
    },
    {
      name: t_('Tool name'),
      field: 'toolName'
    },
    {
      name: t_('D'),
      field: 'D',
      render: (row) => {
        if (row.parameterValueList === undefined) {
          return null
        } else {
          const valueFound = row.parameterValueList.find((parameterValue) => parameterValue.code === 'D')
          return (valueFound !== undefined ? valueFound.value : null)
        }
      }
    },
    {
      name: t_('RC'),
      field: 'Rc',
      render: (row) => {
        if (row.parameterValueList === undefined) {
          return null
        } else {
          const valueFound = row.parameterValueList.find((parameterValue) => parameterValue.code === 'Rc')
          return (valueFound !== undefined ? valueFound.value : null)
        }
      }

    },
    {
      name: t_('CL'),
      field: 'CL',
      render: (row) => {
        if (row.parameterValueList === undefined) {
          return null
        } else {
          const valueFound = row.parameterValueList.find((parameterValue) => parameterValue.code === 'CL')
          return (valueFound !== undefined ? valueFound.value : null)
        }
      }
    },
    {
      name: t_('Z'),
      field: 'Z',
      render: (row) => {
        if (row.parameterValueList === undefined) {
          return null
        } else {
          const valueFound = row.parameterValueList.find((parameterValue) => parameterValue.code === 'Z')
          return (valueFound !== undefined ? valueFound.value : null)
        }
      }
    },
    {
      name: t_('TL'),
      field: 'TL',
      render: (row) => {
        if (row.parameterValueList === undefined) {
          return null
        } else {
          const valueFound = row.parameterValueList.find((parameterValue) => parameterValue.code === 'TL')
          return (valueFound !== undefined ? valueFound.value : null)
        }
      }
    }

  ]

  const turningColumns = [
    {
      name: t_('Code'),
      field: 'code'
    },
    {
      name: t_('Tool name'),
      field: 'toolName'
    },
    {
      name: t_('XL'),
      field: '',
      render: (row) => {
        if (row.parameterValueList === undefined) {
          return null
        } else {
          const valueFound = row.parameterValueList.find((parameterValue) => parameterValue.code === 'XL')
          return (valueFound !== undefined ? valueFound.value : null)
        }
      }
    },
    {
      name: t_('YL'),
      field: '',
      render: (row) => {
        if (row.parameterValueList === undefined) {
          return null
        } else {
          const valueFound = row.parameterValueList.find((parameterValue) => parameterValue.code === 'YL')
          return (valueFound !== undefined ? valueFound.value : null)
        }
      }
    },
    {
      name: t_('ZL'),
      field: '',
      render: (row) => {
        if (row.parameterValueList === undefined) {
          return null
        } else {
          const valueFound = row.parameterValueList.find((parameterValue) => parameterValue.code === 'ZL')
          return (valueFound !== undefined ? valueFound.value : null)
        }
      }
    }
  ]
  const sortOptions = {
    default: {
      field: 'order',
      direction: 1
    }
  }
  const getToolGroups = (toolsByGroups) => {
    return (
      <>
        {toolsByGroups?.map((group) => {
          if (group.name === 'Milling' || group.name === 'Turning') {
            return (
              <DefaultAccordion
                key={group.id}
                headerRender={group.name}
                defaultExpanded={false}
                headerSx={{ backgroundColor: theme.palette.background.paper, marginTop: '1rem' }}
                contentSx={{ backgroundColor: theme.palette.background.paper }}
              >
                <SmartMateriaUITable
                  tableTuneUniqueKey={group.name === 'Milling' ? 'toolTableMilling' : (group.name === 'Turning' ? 'toolTableTurning' : null)}
                  columns={group.name === 'Milling' ? millingColumns : (group.name === 'Turning' ? turningColumns : null)}
                  dataFetch={group.tools}
                />
              </DefaultAccordion>
            )
          }
          return null
        })}
      </>
    )
  }

  return (
    <>
      <DefaultAccordion
        onExpandedStatusChange={(expanded) => {
          assetsInterval(expanded)
        }}
        headerRender={t_('Assets')}
        defaultExpanded={false}
        headerSx={{ backgroundColor: theme => theme.palette.background.default }}
        contentSx={{ backgroundColor: theme => theme.palette.background.default }}
      >
        {assets == null
          ? <Stack sx={{ width: '100%', height: '5vh' }} direction='row' justifyContent='center' alignItems='center'><CircularProgress size='5rem' /></Stack>
          : assets?.length > 0
            ? assets?.map((device) => {
              return (
                <DefaultAccordion
                  key={device.id}
                  headerRender={device.name}
                  defaultExpanded={false}
                  headerSx={{ backgroundColor: theme => theme.palette.background.paper }}
                  contentSx={{ backgroundColor: theme => theme.palette.background.paper }}
                >
                  <SmartMateriaUITable
                    tableTuneUniqueKey='toolTableAsset'
                    columns={toolColumns}
                    dataFetch={device.tools}
                    rowBackgroundColorFunction={
                    row => {
                      return getRowBackgroundColorForTable(row)
                    }
                  }
                    sortOptions={sortOptions}
                  />
                  {getToolGroups(device.toolsByGroups)}
                </DefaultAccordion>
              )
            }
            )
            : (
              <Stack sx={{ width: '100%', height: '5vh' }} direction='row' justifyContent='center' alignItems='center'>
                <Typography color='primary' fontSize='1.5rem'>{t_('No data to display')}</Typography>
              </Stack>
              )}
      </DefaultAccordion>
    </>
  )
}

export default Assets
